import React from 'react'
import { useBasket } from '../state/BasketContext'
import { useTranslation } from '../i18n/LanguageContext'
import { getBasketFoods } from '../scoring'
import { getFoodPrice } from '../types'
import { translateFoodState, foodStateBadgeClass } from '../i18n/nutrientTranslations'
import { translateFoodName, translateFoodCategory } from '../i18n/foodTranslations'

export function BasketView() {
  const { scoreResult, basketOrder, addFood, removeFood, setFoodGrams, clearBasket, moveItemUp, moveItemDown } = useBasket()
  const { t, lang } = useTranslation()

  const basketFoods = getBasketFoods(scoreResult.foodOptions, basketOrder)
  const hasItems = basketFoods.length > 0

  const pricePerGram = scoreResult.totals.weight > 0 ? scoreResult.totals.price / scoreResult.totals.weight : 0
  const pricePer100g = pricePerGram * 100

  return (
    <div className="h-100 d-flex flex-column">
      <div className={`d-flex justify-content-between align-items-center mb-3 ${hasItems ? 'p-2 bg-light rounded-2 border' : ''}`}>
        <h4 className="h5 mb-0">
          <i className="bi bi-basket me-2"></i>
          {t('basket.title')}
        </h4>
        {hasItems && (
          <div className="d-flex align-items-center gap-2 text-muted small">
            <span>{t('basket.weight', { weight: scoreResult.totals.weight.toFixed(0) })}</span>
            <span className="text-muted">|</span>
            <span>{t('basket.total', { price: scoreResult.totals.price.toFixed(0) })}</span>
            <span className="text-muted">|</span>
            <span>{t('basket.per100g', { price: pricePer100g.toFixed(0) })}</span>
          </div>
        )}
        {hasItems && (
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={clearBasket}
            title={t('basket.clearTooltip')}
          >
            <i className="bi bi-basket me-1"></i>{t('basket.clearLabel')}
          </button>
        )}
      </div>

      <div className="flex-grow-1 overflow-auto">
        {!hasItems ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-basket display-6 d-block mb-3 opacity-50"></i>
            <p>{t('basket.empty')}</p>
            <p className="small">{t('basket.emptyHint')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0" style={{ whiteSpace: 'nowrap' }}>
              <thead className="table-light sticky-top">
                <tr>
                  <th className="text-center">{t('basket.column.order')}</th>
                  <th>{t('basket.column.food')}</th>
                  <th>{t('basket.column.category')}</th>
                  <th>{t('basket.column.state')}</th>
                  <th>{t('basket.column.totalWeight')}</th>
                  <th>{t('basket.column.price')}</th>
                  <th>{t('basket.column.portion')}</th>
                  <th>{t('basket.column.weight')}</th>
                  <th className="text-center">{t('basket.column.delete')}</th>
                </tr>
              </thead>
              <tbody>
                {basketFoods.map((option, index) => {
                  const { food, existingGrams, totalWeightInBasket } = option
                  const currentPortions = existingGrams / food.portionGrams

                  const handleGramsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const newGrams = parseInt(e.target.value) || 0
                    setFoodGrams(food.id, newGrams)
                  }

                  const isFirst = index === 0
                  const isLast = index === basketFoods.length - 1

                  return (
                    <tr key={food.id}>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => moveItemUp(food.id)}
                            disabled={isFirst}
                            title={t('basket.tooltip.up')}
                            style={{ padding: '2px 6px', fontSize: '11px' }}
                          >
                            <i className="bi bi-arrow-up"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => moveItemDown(food.id)}
                            disabled={isLast}
                            title={t('basket.tooltip.down')}
                            style={{ padding: '2px 6px', fontSize: '11px' }}
                          >
                            <i className="bi bi-arrow-down"></i>
                          </button>
                        </div>
                      </td>

                      <td>
                        <span className="fw-medium">{translateFoodName(food.name, lang)}</span>
                        {food.highFodmap && <span className="badge bg-warning text-dark ms-1" style={{fontSize: '9px'}}>FODMAP</span>}
                      </td>

                      <td>
                        <small className="text-muted">{translateFoodCategory(food.category, lang)}</small>
                      </td>

                      <td>
                        <span className={`badge ${foodStateBadgeClass(food.state)}`}>{translateFoodState(food.state, lang)}</span>
                      </td>

                      <td>
                        <strong>{totalWeightInBasket.toFixed(0)}g</strong>
                      </td>

                      <td>
                        <strong>{getFoodPrice(food, existingGrams).toFixed(0)}</strong>
                        <small className="text-muted ms-1">Ft</small>
                      </td>

                      <td>
                        <span className="fw-medium">{currentPortions.toFixed(1)}</span>
                        <small className="text-muted ms-1">({food.portionGrams.toFixed(0)}g/p)</small>
                      </td>

                      <td>
                        <div className="d-flex align-items-center">
                          <button
                            className="btn btn-outline-secondary btn-sm me-1"
                            onClick={() => {
                              const newGrams = Math.max(0, existingGrams - food.portionGrams)
                              setFoodGrams(food.id, newGrams)
                            }}
                            title={t('basket.tooltip.removePortion')}
                          >
                            <i className="bi bi-dash"></i>
                          </button>
                          <input
                            type="number"
                            className="form-control form-control-sm me-1"
                            style={{ width: '70px' }}
                            value={Math.round(existingGrams)}
                            onChange={handleGramsChange}
                            min="0"
                            step="1"
                            title={t('basket.tooltip.gramInput')}
                          />
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => addFood(food.id)}
                            title={t('basket.tooltip.addPortion')}
                          >
                            <i className="bi bi-plus"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm ms-1"
                            onClick={() => {
                              const input = window.prompt(t('basket.prompt.addGrams'))
                              if (input !== null) {
                                const gramsToAdd = parseFloat(input)
                                if (!isNaN(gramsToAdd) && gramsToAdd > 0) {
                                  setFoodGrams(food.id, existingGrams + gramsToAdd)
                                }
                              }
                            }}
                            title={t('basket.tooltip.addGramsBtn')}
                          >
                            +g
                          </button>
                        </div>
                      </td>

                      <td className="text-center">
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeFood(food.id)}
                          title={t('basket.tooltip.removeFromBasket')}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
